import {
  BookOpen,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Info,
  Menu,
  Pause,
  Play,
  RefreshCw,
  LogIn,
  LogOut,
  Maximize2,
  Minimize2,
  X,
} from "lucide-react";
import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DemoMarketReplayClient, MarketReplayClient } from "./api.js";
import { loadAppConfig } from "./appConfig.js";
import { MethodologyPage } from "./methodology.jsx";
import { PortfoliosPage } from "./portfolios.jsx";
import {
  STRATEGY_COLORS,
  STRATEGY_DESCRIPTIONS,
  STRATEGY_LINE_STYLES,
  STRATEGY_LABELS,
  SIMULATION_NOTES,
  formatCurrency,
  formatNumber,
  formatPercent,
  decisionsForDate,
  horizonStrategyStats,
  hasStrategyDecisionsForWindow,
  hasStrategyHoldingsForWindow,
  holdingsForDate,
  maxReplaySteps,
  snapshotRows,
  sortedRuns,
  sortedStrategiesForWindow,
  sortedWindows,
  windowSeries,
} from "./replay.js";

const DEMO_USER = {
  email: "local-demo@jestle.com",
  getIdToken: async () => "local-demo-token",
};

const TAX_MARKER_OFFSETS = [
  { x: 10, y: -10 },
  { x: 26, y: -18 },
  { x: -16, y: -18 },
  { x: 18, y: 10 },
  { x: -24, y: 8 },
  { x: 34, y: -4 },
  { x: -32, y: -4 },
];
const TAX_RATE_FALLBACKS = {
  federalLongTerm: 0.2,
  federalNiit: 0.038,
  federalShortTerm: 0.37,
  state: 0.044,
};

const TOPBAR_MENU_ITEMS = [
  {
    href: "/",
    icon: Play,
    key: "replay",
    label: "Replay",
  },
  {
    href: "/methodology",
    icon: BookOpen,
    key: "methodology",
    label: "Methodology",
  },
  {
    href: "/portfolios",
    icon: Briefcase,
    key: "portfolios",
    label: "Portfolios",
  },
];

export default function App() {
  const [activePage, setActivePage] = useState(pageKeyFromLocation);
  const [configState, setConfigState] = useState({ loading: true });
  const [auth, setAuth] = useState(null);
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [runsIndex, setRunsIndex] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [runArtifacts, setRunArtifacts] = useState(null);
  const [seriesPayload, setSeriesPayload] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesError, setSeriesError] = useState("");
  const [selectedWindowId, setSelectedWindowId] = useState("");
  const [selectedHoldingStrategy, setSelectedHoldingStrategy] = useState("");
  const [replayIndex, setReplayIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const [error, setError] = useState("");
  const [holdingsExpanded, setHoldingsExpanded] = useState(false);
  const [expandedHoldingsError, setExpandedHoldingsError] = useState("");
  const [expandedHoldingsLoading, setExpandedHoldingsLoading] = useState(false);
  const [expandedHoldingsPayload, setExpandedHoldingsPayload] = useState(null);
  const [decisionsExpanded, setDecisionsExpanded] = useState(false);
  const [expandedDecisionsError, setExpandedDecisionsError] = useState("");
  const [expandedDecisionsLoading, setExpandedDecisionsLoading] = useState(false);
  const [expandedDecisionsPayload, setExpandedDecisionsPayload] = useState(null);
  const [holdingsMenuOpen, setHoldingsMenuOpen] = useState(false);
  const [topbarMenuOpen, setTopbarMenuOpen] = useState(false);
  const [strategyInfo, setStrategyInfo] = useState("");
  const [simulationInfoOpen, setSimulationInfoOpen] = useState(false);
  const playbackTimerRef = useRef(null);
  const topbarMenuRef = useRef(null);

  useEffect(() => {
    const handlePopState = () => setActivePage(pageKeyFromLocation());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    let unsubscribe = () => {};
    loadAppConfig()
      .then((config) => {
        if (config.demoMode) {
          setUser(DEMO_USER);
          setConfigState({ loading: false, config });
          return;
        }
        const firebaseApp = initializeApp(config);
        const firebaseAuth = getAuth(firebaseApp);
        unsubscribe = onAuthStateChanged(firebaseAuth, setUser);
        setAuth(firebaseAuth);
        setConfigState({ loading: false, config });
      })
      .catch((configError) => {
        setConfigState({ loading: false, error: configError.message });
      });
    return () => unsubscribe();
  }, []);

  const isDemo = Boolean(configState.config?.demoMode);
  const client = useMemo(() => {
    if (!configState.config || (!user && !isDemo)) {
      return null;
    }
    if (isDemo) {
      return new DemoMarketReplayClient({
        artifactBase: configState.config.localArtifactBase,
      });
    }
    return new MarketReplayClient({
      baseUrl: configState.config.marketReplayApiBase,
      getToken: () => user.getIdToken(),
    });
  }, [configState.config, isDemo, user]);
  const isReplayPage = activePage === "replay";

  const refreshRuns = useCallback(async () => {
    if (!client) {
      return;
    }
    setError("");
    const [sessionPayload, runsPayload] = await Promise.all([
      client.session(),
      client.runs(),
    ]);
    setSession(sessionPayload);
    setRunsIndex(runsPayload);
    const runs = sortedRuns(runsPayload);
    setSelectedRunId((current) => current || runs[0]?.run_id || "");
  }, [client]);

  useEffect(() => {
    if (!client) {
      setSession(null);
      setRunsIndex(null);
      return;
    }
    refreshRuns().catch((loadError) => setError(loadError.message));
  }, [client, refreshRuns]);

  useEffect(() => {
    if (!client || !selectedRunId || !isReplayPage) {
      setRunArtifacts(null);
      setSeriesPayload(null);
      return;
    }
    let active = true;
    setError("");
    Promise.all([
      client.run(selectedRunId),
      client.windows(selectedRunId),
      client.costs(selectedRunId),
      client.taxes(selectedRunId),
      client.holdings(selectedRunId),
      client.decisions(selectedRunId).catch(() => null),
    ])
      .then(([manifest, windows, costs, taxes, holdings, decisions]) => {
        if (!active) {
          return;
        }
        setRunArtifacts({
          costs,
          decisions,
          holdings,
          manifest,
          taxes,
          windows,
        });
        setSeriesPayload(null);
        const orderedWindows = sortedWindows(windows);
        setSelectedWindowId((current) => current || orderedWindows[0]?.window_id);
        setReplayIndex(0);
      })
      .catch((loadError) => setError(loadError.message));
    return () => {
      active = false;
    };
  }, [client, isReplayPage, selectedRunId]);

  useEffect(() => {
    if (!client || !selectedRunId || !selectedWindowId || !isReplayPage) {
      setSeriesPayload(null);
      setSeriesLoading(false);
      setSeriesError("");
      return undefined;
    }
    let active = true;
    const requestKey = [selectedRunId, selectedWindowId].join("|");
    setSeriesPayload(null);
    setSeriesError("");
    setSeriesLoading(true);
    client
      .series(selectedRunId, selectedWindowId)
      .then((payload) => {
        if (active) {
          setSeriesPayload({
            ...payload,
            request_key: requestKey,
          });
          setReplayIndex(0);
        }
      })
      .catch((loadError) => {
        if (active) {
          setSeriesError(loadError.message);
        }
      })
      .finally(() => {
        if (active) {
          setSeriesLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [client, isReplayPage, selectedRunId, selectedWindowId]);

  const runs = useMemo(() => sortedRuns(runsIndex), [runsIndex]);
  const windows = useMemo(
    () => sortedWindows(runArtifacts?.windows),
    [runArtifacts],
  );
  const horizonStats = useMemo(
    () => horizonStrategyStats(runArtifacts?.windows),
    [runArtifacts],
  );
  const seriesByStrategy = useMemo(
    () => windowSeries(seriesPayload, selectedWindowId),
    [seriesPayload, selectedWindowId],
  );
  const maxSteps = useMemo(
    () => maxReplaySteps(seriesByStrategy),
    [seriesByStrategy],
  );
  const currentDate = replayDate(seriesByStrategy, replayIndex);
  const currentRows = useMemo(
    () =>
      snapshotRows({
        costsByStrategy: runArtifacts?.costs?.windows?.[selectedWindowId],
        currentDate,
        decisionsByStrategy: runArtifacts?.decisions?.windows?.[selectedWindowId],
        replayIndex,
        seriesByStrategy,
      }),
    [currentDate, runArtifacts, replayIndex, selectedWindowId, seriesByStrategy],
  );
  const holdingStrategies = useMemo(() => {
    const artifactStrategies = sortedStrategiesForWindow(
      runArtifacts?.holdings,
      selectedWindowId,
    );
    if (artifactStrategies.length) {
      return artifactStrategies;
    }
    return currentRows.map((row) => row.strategy).filter(Boolean);
  }, [currentRows, runArtifacts, selectedWindowId]);
  useEffect(() => {
    if (!holdingStrategies.includes(selectedHoldingStrategy)) {
      setSelectedHoldingStrategy(holdingStrategies[0] || "");
    }
  }, [holdingStrategies, selectedHoldingStrategy]);
  const previewArtifactHasSelectedStrategy = hasStrategyHoldingsForWindow(
    runArtifacts?.holdings,
    selectedWindowId,
    selectedHoldingStrategy,
  );
  const expandedHoldingsKey = [
    selectedRunId,
    selectedWindowId,
    selectedHoldingStrategy,
  ].join("|");
  const expandedHoldingsPayloadMatches =
    expandedHoldingsPayload?.request_key === expandedHoldingsKey;
  const expandedHoldings = holdingsForDate(
    expandedHoldingsPayloadMatches ? expandedHoldingsPayload : null,
    selectedWindowId,
    currentDate,
    selectedHoldingStrategy,
    Infinity,
  );
  const previewHoldings = previewArtifactHasSelectedStrategy
    ? holdingsForDate(
        runArtifacts?.holdings,
        selectedWindowId,
        currentDate,
        selectedHoldingStrategy,
        5,
      )
    : expandedHoldings.slice(0, 5);
  const displayedHoldings =
    holdingsExpanded && expandedHoldingsPayloadMatches
      ? expandedHoldings
      : previewHoldings;
  const previewArtifactHasSelectedDecisions = hasStrategyDecisionsForWindow(
    runArtifacts?.decisions,
    selectedWindowId,
    selectedHoldingStrategy,
  );
  const expandedDecisionsKey = [
    selectedRunId,
    selectedWindowId,
    selectedHoldingStrategy,
  ].join("|");
  const expandedDecisionsPayloadMatches =
    expandedDecisionsPayload?.request_key === expandedDecisionsKey;
  const expandedDecisions = decisionsForDate(
    expandedDecisionsPayloadMatches ? expandedDecisionsPayload : null,
    selectedWindowId,
    currentDate,
    selectedHoldingStrategy,
    Infinity,
  );
  const previewDecisions = previewArtifactHasSelectedDecisions
    ? decisionsForDate(
        runArtifacts?.decisions,
        selectedWindowId,
        currentDate,
        selectedHoldingStrategy,
        8,
      )
    : expandedDecisions;
  const fullDecisionPreview = {
    rows: expandedDecisions.rows.slice(0, 8),
    summary: expandedDecisions.summary,
  };
  const displayedDecisions =
    decisionsExpanded && expandedDecisionsPayloadMatches
      ? expandedDecisions
      : previewDecisions.rows.length
        ? previewDecisions
        : fullDecisionPreview.rows.length
          ? fullDecisionPreview
      : previewDecisions;
  useEffect(() => {
    if (previewArtifactHasSelectedStrategy && !holdingsExpanded) {
      return undefined;
    }
    if (
      !client
      || !isReplayPage
      || !selectedRunId
      || !selectedWindowId
      || !selectedHoldingStrategy
    ) {
      return undefined;
    }
    let active = true;
    const requestKey = [
      selectedRunId,
      selectedWindowId,
      selectedHoldingStrategy,
    ].join("|");
    setExpandedHoldingsError("");
    setExpandedHoldingsPayload(null);
    setExpandedHoldingsLoading(true);
    client
      .fullHoldings(
        selectedRunId,
        selectedWindowId,
        selectedHoldingStrategy,
      )
      .then((payload) => {
        if (active) {
          setExpandedHoldingsPayload({
            ...payload,
            request_key: requestKey,
          });
        }
      })
      .catch((holdingsError) => {
        if (active) {
          setExpandedHoldingsError(holdingsError.message);
        }
      })
      .finally(() => {
        if (active) {
          setExpandedHoldingsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [
    client,
    holdingsExpanded,
    isReplayPage,
    previewArtifactHasSelectedStrategy,
    selectedHoldingStrategy,
    selectedRunId,
    selectedWindowId,
  ]);

  useEffect(() => {
    if (
      previewArtifactHasSelectedDecisions &&
      previewDecisions.rows.length &&
      !decisionsExpanded
    ) {
      return undefined;
    }
    if (
      !client
      || !isReplayPage
      || !selectedRunId
      || !selectedWindowId
      || !selectedHoldingStrategy
    ) {
      return undefined;
    }
    let active = true;
    const requestKey = [
      selectedRunId,
      selectedWindowId,
      selectedHoldingStrategy,
    ].join("|");
    setExpandedDecisionsError("");
    setExpandedDecisionsPayload(null);
    setExpandedDecisionsLoading(true);
    client
      .fullDecisions(selectedRunId, selectedWindowId, selectedHoldingStrategy)
      .then((payload) => {
        if (active) {
          setExpandedDecisionsPayload({
            ...payload,
            request_key: requestKey,
          });
        }
      })
      .catch((decisionsError) => {
        if (active) {
          setExpandedDecisionsError(decisionsError.message);
        }
      })
      .finally(() => {
        if (active) {
          setExpandedDecisionsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [
    client,
    decisionsExpanded,
    isReplayPage,
    previewArtifactHasSelectedDecisions,
    previewDecisions.rows.length,
    selectedHoldingStrategy,
    selectedRunId,
    selectedWindowId,
  ]);

  useEffect(() => {
    if (!strategyInfo && !simulationInfoOpen) {
      return undefined;
    }
    const closeInfoDialogs = (event) => {
      if (event.key === "Escape") {
        setStrategyInfo("");
        setSimulationInfoOpen(false);
      }
    };
    window.addEventListener("keydown", closeInfoDialogs);
    return () => window.removeEventListener("keydown", closeInfoDialogs);
  }, [simulationInfoOpen, strategyInfo]);

  useEffect(() => {
    if (!topbarMenuOpen) {
      return undefined;
    }
    const closeTopbarMenu = (event) => {
      if (event.key === "Escape") {
        setTopbarMenuOpen(false);
        return;
      }
      if (
        event.type === "mousedown" &&
        topbarMenuRef.current &&
        !topbarMenuRef.current.contains(event.target)
      ) {
        setTopbarMenuOpen(false);
      }
    };
    window.addEventListener("keydown", closeTopbarMenu);
    window.addEventListener("mousedown", closeTopbarMenu);
    return () => {
      window.removeEventListener("keydown", closeTopbarMenu);
      window.removeEventListener("mousedown", closeTopbarMenu);
    };
  }, [topbarMenuOpen]);

  const toggleHoldings = useCallback(() => {
    setHoldingsExpanded((current) => !current);
  }, []);

  const toggleDecisions = useCallback(() => {
    setDecisionsExpanded((current) => !current);
  }, []);

  const selectHoldingStrategy = useCallback((strategy) => {
    setExpandedHoldingsError("");
    setExpandedHoldingsLoading(false);
    setExpandedHoldingsPayload(null);
    setExpandedDecisionsError("");
    setExpandedDecisionsLoading(false);
    setExpandedDecisionsPayload(null);
    setHoldingsMenuOpen(false);
    setSelectedHoldingStrategy(strategy);
  }, []);

  const clearPlaybackTimer = useCallback(() => {
    if (playbackTimerRef.current !== null) {
      window.clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
  }, []);

  const stopPlayback = useCallback(() => {
    clearPlaybackTimer();
    setPlaying(false);
  }, [clearPlaybackTimer]);

  useEffect(() => {
    setReplayIndex(0);
    setHoldingsExpanded(false);
    setHoldingsMenuOpen(false);
    setExpandedHoldingsError("");
    setExpandedHoldingsLoading(false);
    setExpandedHoldingsPayload(null);
    setDecisionsExpanded(false);
    setExpandedDecisionsError("");
    setExpandedDecisionsLoading(false);
    setExpandedDecisionsPayload(null);
    stopPlayback();
  }, [selectedRunId, selectedWindowId, stopPlayback]);

  useEffect(() => {
    return () => clearPlaybackTimer();
  }, [clearPlaybackTimer]);

  useEffect(() => {
    setReplayIndex((current) => Math.min(current, Math.max(0, maxSteps - 1)));
    if (maxSteps <= 1) {
      stopPlayback();
    }
  }, [maxSteps, stopPlayback]);

  const startPlayback = useCallback(() => {
    if (maxSteps <= 1) {
      return;
    }
    clearPlaybackTimer();
    setReplayIndex((current) => (current >= maxSteps - 1 ? 0 : current));
    setPlaying(true);
    playbackTimerRef.current = window.setInterval(() => {
      setReplayIndex((current) => {
        if (current >= maxSteps - 1) {
          stopPlayback();
          return current;
        }
        const next = Math.min(current + 1, maxSteps - 1);
        if (next >= maxSteps - 1) {
          stopPlayback();
        }
        return next;
      });
    }, Math.max(120, 1200 / speed));
  }, [clearPlaybackTimer, maxSteps, speed, stopPlayback]);

  const stepBack = useCallback(() => {
    stopPlayback();
    setReplayIndex((current) => Math.max(0, current - 1));
  }, [stopPlayback]);

  const stepForward = useCallback(() => {
    stopPlayback();
    setReplayIndex((current) => Math.min(maxSteps - 1, current + 1));
  }, [maxSteps, stopPlayback]);

  useEffect(() => {
    const moveReplayWithArrowKeys = (event) => {
      if (isFormControl(event.target)) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        stepBack();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        stepForward();
      }
    };
    window.addEventListener("keydown", moveReplayWithArrowKeys);
    return () =>
      window.removeEventListener("keydown", moveReplayWithArrowKeys);
  }, [stepBack, stepForward]);

  const togglePlayback = useCallback(() => {
    if (playing) {
      stopPlayback();
      return;
    }
    startPlayback();
  }, [playing, startPlayback, stopPlayback]);

  const chooseReplayIndex = useCallback((index) => {
    stopPlayback();
    setReplayIndex(Math.max(0, Math.min(maxSteps - 1, index)));
  }, [maxSteps, stopPlayback]);

  const selectTopbarPage = useCallback((pageKey) => {
    const item = TOPBAR_MENU_ITEMS.find((menuItem) => menuItem.key === pageKey);
    if (!item) {
      return;
    }
    setTopbarMenuOpen(false);
    if (pageKey !== "replay") {
      stopPlayback();
    }
    window.history.pushState({}, "", item.href);
    setActivePage(pageKey);
  }, [stopPlayback]);

  const signIn = async () => {
    setError("");
    if (isDemo) {
      setUser(DEMO_USER);
      return;
    }
    await signInWithPopup(auth, new GoogleAuthProvider()).catch((signInError) =>
      setError(signInError.message),
    );
  };

  const signOutUser = async () => {
    stopPlayback();
    if (isDemo) {
      setUser(null);
      return;
    }
    await signOut(auth);
  };

  if (configState.loading) {
    return <Shell status="Loading configuration" />;
  }
  if (configState.error) {
    return <Shell status={configState.error} />;
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <h1>Market Replay</h1>
          <p>
            {isDemo
              ? "Local demo mode, Google login bypassed"
              : session?.email || "Private simulation viewer"}
          </p>
        </div>
        <div className="topbar-actions">
          {user ? (
            <>
              <TopbarMenu
                activePage={activePage}
                items={TOPBAR_MENU_ITEMS}
                menuOpen={topbarMenuOpen}
                menuRef={topbarMenuRef}
                onSelect={selectTopbarPage}
                onToggle={() => setTopbarMenuOpen((current) => !current)}
              />
              <button
                aria-label="Refresh runs"
                className="icon-button tooltip-button"
                data-tooltip="Refresh runs"
                onClick={refreshRuns}
                type="button"
              >
                <RefreshCw size={18} />
              </button>
              <button
                aria-label={isDemo ? "Exit demo" : "Sign out"}
                className="icon-button tooltip-button"
                data-tooltip={isDemo ? "Exit demo" : "Sign out"}
                onClick={signOutUser}
                type="button"
              >
                <LogOut size={18} />
              </button>
            </>
          ) : null}
        </div>
      </header>

      {error ? <div className="notice">{error}</div> : null}

      {user && activePage === "methodology" ? (
        <MethodologyPage client={client} />
      ) : user && activePage === "portfolios" ? (
        <PortfoliosPage client={client} />
      ) : user ? (
        <div className="workspace">
          <aside className="side-panel">
            <label>
              Run
              <select
                onChange={(event) => {
                  setSelectedRunId(event.target.value);
                  setSelectedWindowId("");
                  stopPlayback();
                }}
                value={selectedRunId}
              >
                {runs.map((run) => (
                  <option key={run.run_id} value={run.run_id}>
                    {run.title || run.run_id}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Window
              <select
                onChange={(event) => {
                  setSelectedWindowId(event.target.value);
                  setReplayIndex(0);
                  stopPlayback();
                }}
                value={selectedWindowId}
              >
                {windows.map((windowRow) => (
                  <option key={windowRow.window_id} value={windowRow.window_id}>
                    {windowRow.window_years}Y {windowRow.window_start} to{" "}
                    {windowRow.window_end}
                  </option>
                ))}
              </select>
            </label>
            <ReplayControls
              maxSteps={maxSteps}
              onReplayIndexChange={chooseReplayIndex}
              onSimulationInfo={() => setSimulationInfoOpen(true)}
              onStepBack={stepBack}
              onStepForward={stepForward}
              onTogglePlayback={togglePlayback}
              playing={playing}
              replayIndex={replayIndex}
              setSpeed={setSpeed}
              speed={speed}
            />
            <HorizonStatsPanel stats={horizonStats} />
          </aside>

          <section className="main-panel">
            <SummaryStrip onStrategyInfo={setStrategyInfo} rows={currentRows} />
            {seriesError ? (
              <div className="notice">
                Replay series unavailable: {seriesError}
              </div>
            ) : null}
            {seriesLoading ? (
              <div className="holdings-loading">Loading replay series.</div>
            ) : null}
            <PortfolioChart
              onReplayIndexChange={chooseReplayIndex}
              replayIndex={replayIndex}
              seriesByStrategy={seriesByStrategy}
            />
            <StrategyTable
              currentDate={currentDate}
              onStrategyInfo={setStrategyInfo}
              rows={currentRows}
            />
          </section>

          <aside className="holdings-panel">
            <div className="panel-title-row">
              <h2>{holdingsExpanded ? "Holdings" : "Top Holdings"}</h2>
              <div className="holdings-actions">
                <div className="strategy-menu">
                  <button
                    aria-expanded={holdingsMenuOpen}
                    aria-haspopup="listbox"
                    aria-label="Holdings strategy"
                    className="strategy-menu-button"
                    onClick={() => setHoldingsMenuOpen((current) => !current)}
                    type="button"
                  >
                    <span>
                      {STRATEGY_LABELS[selectedHoldingStrategy] ||
                        selectedHoldingStrategy ||
                        "No strategies"}
                    </span>
                    <ChevronDown size={16} />
                  </button>
                  {holdingsMenuOpen ? (
                    <div
                      aria-label="Holdings strategy options"
                      className="strategy-menu-popover"
                      role="listbox"
                    >
                      {holdingStrategies.map((strategy) => (
                        <button
                          aria-selected={strategy === selectedHoldingStrategy}
                          className="strategy-menu-option"
                          key={strategy}
                          onClick={() => selectHoldingStrategy(strategy)}
                          role="option"
                          type="button"
                        >
                          {STRATEGY_LABELS[strategy] || strategy}
                        </button>
                      ))}
                      {!holdingStrategies.length ? (
                        <div className="strategy-menu-empty">
                          Holdings strategies are loading
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <button
                  aria-label={
                    holdingsExpanded ? "Collapse holdings" : "Expand holdings"
                  }
                  className="icon-button tooltip-button"
                  data-tooltip={
                    holdingsExpanded ? "Show top holdings" : "Show all holdings"
                  }
                  disabled={!previewHoldings.length}
                  onClick={toggleHoldings}
                  type="button"
                >
                  {holdingsExpanded ? (
                    <Minimize2 size={18} />
                  ) : (
                    <Maximize2 size={18} />
                  )}
                </button>
              </div>
            </div>
            <p className="muted">
              {previewHoldings.length
                ? holdingsExpanded && !expandedHoldingsLoading
                  ? `${formatNumber(displayedHoldings.length)} holdings`
                  : "Top 5 preview"
                : "No holdings for this step"}
            </p>
            {expandedHoldingsError ? (
              <p className="dialog-error">{expandedHoldingsError}</p>
            ) : null}
            {expandedHoldingsLoading ? (
              <div className="holdings-loading">Loading holdings.</div>
            ) : null}
            {displayedHoldings.length ? (
              <table
                key={`${selectedHoldingStrategy}-${currentDate}-${holdingsExpanded}`}
              >
                <thead>
                  <tr>
                    <th>Ticker</th>
                    <th>Weight</th>
                    <th>Value</th>
                    {holdingsExpanded ? <th>Shares</th> : null}
                    {holdingsExpanded ? <th>Price</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {displayedHoldings.map((holding, index) => (
                    <tr key={`${holding.ticker || holding.symbol}-${index}`}>
                      <td>{holding.ticker || holding.symbol}</td>
                      <td>{formatPercent(holding.weight)}</td>
                      <td>{formatCurrency(holding.value)}</td>
                      {holdingsExpanded ? (
                        <td>{formatNumber(holding.shares)}</td>
                      ) : null}
                      {holdingsExpanded ? (
                        <td>{formatCurrency(holding.price)}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {!expandedHoldingsLoading && !displayedHoldings.length ? (
              <div className="empty-state">No holdings artifact for this run.</div>
            ) : null}
            <DecisionPanel
              decisions={displayedDecisions}
              expanded={decisionsExpanded}
              loading={expandedDecisionsLoading}
              onToggleExpanded={toggleDecisions}
              error={expandedDecisionsError}
            />
          </aside>
        </div>
      ) : (
        <section className="auth-panel">
          <button className="primary-button" onClick={signIn} type="button">
            <LogIn size={18} />
            {isDemo ? "Enter demo" : "Google login"}
          </button>
        </section>
      )}
      {strategyInfo ? (
        <StrategyInfoDialog
          onClose={() => setStrategyInfo("")}
          strategy={strategyInfo}
        />
      ) : null}
      {simulationInfoOpen ? (
        <SimulationInfoDialog onClose={() => setSimulationInfoOpen(false)} />
      ) : null}
    </main>
  );
}

function TopbarMenu({ activePage, items, menuOpen, menuRef, onSelect, onToggle }) {
  return (
    <nav aria-label="Market resources" className="topbar-menu" ref={menuRef}>
      <button
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        aria-label="Market menu"
        className="icon-button tooltip-button"
        data-tooltip="Market menu"
        onClick={onToggle}
        type="button"
      >
        <Menu size={18} />
      </button>
      {menuOpen ? (
        <div
          aria-label="Market menu items"
          className="topbar-menu-popover"
          role="menu"
        >
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <a
                aria-current={activePage === item.key ? "page" : undefined}
                className="topbar-menu-item"
                href={item.href}
                key={item.key}
                onClick={(event) => {
                  event.preventDefault();
                  onSelect(item.key);
                }}
                role="menuitem"
              >
                <Icon size={16} />
                <span>{item.label}</span>
              </a>
            );
          })}
        </div>
      ) : null}
    </nav>
  );
}

function Shell({ status }) {
  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <h1>Market Replay</h1>
          <p>{status}</p>
        </div>
      </header>
    </main>
  );
}

function ReplayControls({
  maxSteps,
  onSimulationInfo,
  onReplayIndexChange,
  onStepBack,
  onStepForward,
  onTogglePlayback,
  playing,
  replayIndex,
  setSpeed,
  speed,
}) {
  return (
    <div className="control-stack">
      <div className="transport">
        <button
          aria-label="Simulation details"
          className="icon-button tooltip-button"
          data-tooltip="Simulation details"
          onClick={onSimulationInfo}
          type="button"
        >
          <Info size={18} />
        </button>
        <button
          aria-label="Previous replay step"
          className="icon-button tooltip-button"
          data-tooltip="Previous step"
          disabled={replayIndex <= 0}
          onClick={onStepBack}
          type="button"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          aria-label={playing ? "Pause" : "Play"}
          className="icon-button tooltip-button"
          data-tooltip={playing ? "Pause replay" : "Play replay"}
          disabled={maxSteps <= 1}
          onClick={onTogglePlayback}
          type="button"
        >
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </button>
        <button
          aria-label="Next replay step"
          className="icon-button tooltip-button"
          data-tooltip="Next step"
          disabled={replayIndex >= maxSteps - 1}
          onClick={onStepForward}
          type="button"
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <label>
        Step
        <input
          max={maxSteps}
          min="1"
          onChange={(event) => {
            const value = Number(event.target.value) - 1;
            if (Number.isFinite(value)) {
              onReplayIndexChange(value);
            }
          }}
          type="range"
          value={Math.min(replayIndex + 1, maxSteps)}
        />
      </label>
      <label>
        Speed
        <input
          max="8"
          min="0.5"
          onChange={(event) => setSpeed(Number(event.target.value))}
          step="0.5"
          type="range"
          value={speed}
        />
      </label>
      <p className="muted">
        Step {Math.min(replayIndex + 1, maxSteps)} of {maxSteps}
      </p>
    </div>
  );
}

function HorizonStatsPanel({ stats }) {
  if (!stats.length) {
    return null;
  }
  return (
    <section className="horizon-stats-panel" aria-label="Return stats">
      <div className="horizon-stats-header">
        <h2>Return Stats</h2>
        <p>
          Mean, lower-tail, max, average total trades, and average intermediate
          rebalance trades across replay windows. Index benchmarks have no
          simulated trades or taxes.
        </p>
      </div>
      {stats.map((horizon) => {
        const winningMean = Math.max(
          ...horizon.strategies
            .map((row) => row.meanReturn)
            .filter((value) => Number.isFinite(value)),
        );
        return (
          <div className="horizon-stat-block" key={horizon.horizon}>
            <h3>{formatNumber(horizon.horizon)}Y windows</h3>
            <div className="horizon-stats-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Strategy</th>
                    <th>Mean</th>
                    <th>Min</th>
                    <th>P05</th>
                    <th>Max</th>
                    <th>Avg trades</th>
                    <th>Avg intermediate</th>
                  </tr>
                </thead>
                <tbody>
                  {horizon.strategies.map((row) => {
                    const isWinner =
                      Number.isFinite(row.meanReturn) &&
                      row.meanReturn === winningMean;
                    return (
                      <tr
                        className={isWinner ? "horizon-stats-winning" : ""}
                        key={row.strategy}
                      >
                        <td>{row.label}</td>
                        <td>{formatPercent(row.meanReturn)}</td>
                        <td>{formatPercent(row.minReturn)}</td>
                        <td>{formatPercent(row.p05Return)}</td>
                        <td>{formatPercent(row.maxReturn)}</td>
                        <td>{formatNumber(row.meanTrades)}</td>
                        <td>{formatNumber(row.meanIntermediateTrades)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function SummaryStrip({ onStrategyInfo, rows }) {
  const finiteReturns = rows
    .map((row) => row.returnValue)
    .filter((value) => Number.isFinite(value));
  const winningReturn = finiteReturns.length ? Math.max(...finiteReturns) : null;
  const winningCount =
    winningReturn === null
      ? 0
      : finiteReturns.filter((value) => value === winningReturn).length;
  return (
    <div className="summary-strip">
      {rows.map((row) => {
        const isWinner =
          winningCount === 1 &&
          Number.isFinite(row.returnValue) &&
          row.returnValue === winningReturn;
        return (
          <div
            className={`metric-panel ${isWinner ? "metric-panel-winning" : ""}`}
            key={row.strategy}
          >
            <span className="metric-title">
              <span className="metric-title-label">{row.label}</span>
              <StrategyInfoButton
                onClick={() => onStrategyInfo(row.strategy)}
                strategy={row.strategy}
              />
            </span>
            <strong>{formatPercent(row.returnValue)}</strong>
            <small>{formatCurrency(row.portfolioValue)}</small>
          </div>
        );
      })}
    </div>
  );
}

function DecisionPanel({ decisions, error, expanded, loading, onToggleExpanded }) {
  const rows = decisions?.rows || [];
  const summary = decisions?.summary || {};
  const hasRows = rows.length > 0;
  const totalRows = Number(summary.displayed_rows || rows.length || 0);
  const canExpand = hasRows && (expanded || totalRows > rows.length);
  return (
    <section className="decision-panel">
      <div className="panel-title-row">
        <h2>Rebalance Decisions</h2>
        <button
          aria-label={expanded ? "Collapse decisions" : "Expand decisions"}
          className="icon-button tooltip-button"
          data-tooltip={expanded ? "Show decision preview" : "Show all decisions"}
          disabled={!canExpand}
          onClick={onToggleExpanded}
          type="button"
        >
          {expanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
        </button>
      </div>
      <div className="decision-summary-grid">
        <DecisionSummaryItem label="Candidates" value={summary.candidate_count} />
        <DecisionSummaryItem label="Targets" value={summary.target_count} />
        <DecisionSummaryItem label="Buys" value={summary.buy_count} />
        <DecisionSummaryItem label="Sells" value={summary.sell_count} />
        <DecisionSummaryItem label="Skips" value={summary.skip_count} />
        <DecisionSummaryItem label="Holds" value={summary.hold_count} />
      </div>
      {summary.row_limit_applied && !expanded ? (
        <p className="table-note">
          Preview is capped; expand to load the full decision artifact.
        </p>
      ) : null}
      {error ? <p className="dialog-error">{error}</p> : null}
      {loading ? <div className="holdings-loading">Loading decisions.</div> : null}
      {hasRows ? (
        <div className="decision-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Decision</th>
                <th>Reason</th>
                <th>Rank</th>
                <th>Score</th>
                <th>Shares</th>
                <th>Trade</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row.ticker}-${row.decision}-${index}`}>
                  <td>{row.ticker}</td>
                  <td>
                    <span
                      className={`decision-pill decision-${String(
                        row.decision,
                      ).toLowerCase()}`}
                    >
                      {row.decision || "na"}
                    </span>
                  </td>
                  <td>{decisionReason(row)}</td>
                  <td>{formatNumber(row.rank)}</td>
                  <td>{formatScore(row.trade_score)}</td>
                  <td>{formatSignedNumber(row.shares_delta)}</td>
                  <td>{formatCurrency(row.trade_dollars)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !loading ? (
        <div className="empty-state decision-empty">
          No rebalance decisions for this step.
        </div>
      ) : null}
    </section>
  );
}

function DecisionSummaryItem({ label, value }) {
  return (
    <span className="decision-summary-item">
      <small>{label}</small>
      <strong>{formatNumber(value || 0)}</strong>
    </span>
  );
}

function StrategyTable({ currentDate, onStrategyInfo, rows }) {
  return (
    <div className="table-panel">
      <h2>Strategy Metrics {currentDate ? `for ${currentDate}` : ""}</h2>
      <p className="table-note">
        Values are cumulative through the selected replay step. Stock-strategy
        returns include modeled transaction costs and taxes; Nasdaq index
        benchmarks are scaled index values without simulated trades, costs, or
        taxes.
      </p>
      <table>
        <thead>
          <tr>
            <th>Strategy</th>
            <th>Return</th>
            <th>Value</th>
            <th>Cum costs</th>
            <th>Cum tax paid</th>
            <th>Cum trades</th>
            <th>Intermediate trades</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.strategy}>
              <td>
                <span className="strategy-cell">
                  <StrategyLineSample strategy={row.strategy} />
                  {row.label}
                  <StrategyInfoButton
                    onClick={() => onStrategyInfo(row.strategy)}
                    strategy={row.strategy}
                  />
                </span>
              </td>
              <td>{formatPercent(row.returnValue)}</td>
              <td>{formatCurrency(row.portfolioValue)}</td>
              <td>{formatCurrency(row.transactionCost)}</td>
              <td>{formatCurrency(row.taxes)}</td>
              <td>{formatNumber(row.trades)}</td>
              <td>{formatNumber(row.intermediateTrades)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PortfolioChart({
  onReplayIndexChange,
  replayIndex,
  seriesByStrategy,
}) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [taxHover, setTaxHover] = useState(null);
  useEffect(() => {
    setHoverIndex(null);
  }, [replayIndex]);
  const strategies = Object.keys(seriesByStrategy);
  const points = strategies.flatMap((strategy) =>
    seriesByStrategy[strategy]
      .filter((point) => Number.isFinite(Number(point.portfolio_value)))
      .map((point, index) => ({
        ...point,
        index,
        portfolio_value: Number(point.portfolio_value),
        strategy,
      })),
  );
  if (!points.length) {
    return <div className="chart-panel empty-state">No series data.</div>;
  }
  const width = 920;
  const height = 320;
  const padding = {
    bottom: 50,
    left: 88,
    right: 28,
    top: 28,
  };
  const maxIndex = Math.max(1, ...points.map((point) => point.index));
  const minValue = Math.min(...points.map((point) => point.portfolio_value));
  const maxValue = Math.max(...points.map((point) => point.portfolio_value));
  const valueRange = Math.max(1, maxValue - minValue);
  const x = (index) =>
    padding.left +
    (index / maxIndex) * (width - padding.left - padding.right);
  const y = (value) =>
    height -
    padding.bottom -
    ((value - minValue) / valueRange) *
      (height - padding.top - padding.bottom);
  const yTicks = Array.from({ length: 5 }, (_, index) => {
    const ratio = index / 4;
    return minValue + valueRange * ratio;
  });
  const dateByIndex = new Map();
  for (const strategy of strategies) {
    for (const [index, point] of (seriesByStrategy[strategy] || []).entries()) {
      if (!dateByIndex.has(index) && point.date) {
        dateByIndex.set(index, point.date);
      }
    }
  }
  const xTickIndices = Array.from(
    new Set([0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(maxIndex * ratio))),
  ).filter((index) => index >= 0 && index <= maxIndex);
  const cursorIndex = hoverIndex ?? replayIndex;
  const hasFinalLiquidation = strategies.some((strategy) => {
    const fullSeries = seriesByStrategy[strategy] || [];
    return isFinalLiquidationPoint(
      strategy,
      fullSeries[fullSeries.length - 1],
      fullSeries.length - 1,
      fullSeries,
    );
  });
  const hasTaxPayments = strategies.some((strategy) =>
    (seriesByStrategy[strategy] || []).some(
      (point) => taxPaymentAmount(point) > 0,
    ),
  );
  const taxMarkerPosition = (index, value, strategyIndex) => {
    const offset =
      TAX_MARKER_OFFSETS[strategyIndex % TAX_MARKER_OFFSETS.length];
    return {
      x: Math.max(
        padding.left + 10,
        Math.min(width - padding.right - 10, x(index) + offset.x),
      ),
      y: Math.max(
        padding.top + 10,
        Math.min(height - padding.bottom - 10, y(value) + offset.y),
      ),
    };
  };
  const stepIndexFromEvent = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const scaledX = ((event.clientX - bounds.left) / bounds.width) * width;
    const nextIndex = Math.round(
      ((scaledX - padding.left) / (width - padding.left - padding.right)) *
        maxIndex,
    );
    return Math.max(0, Math.min(maxIndex, nextIndex));
  };
  const selectReplayIndexFromEvent = (event) => {
    const nextIndex = stepIndexFromEvent(event);
    setHoverIndex(nextIndex);
    onReplayIndexChange(nextIndex);
  };
  const showTaxTooltip = (event, strategy, point, taxPaid) => {
    const tooltipWidth = 260;
    const tooltipHeight = 132;
    setTaxHover({
      left: Math.max(
        12,
        Math.min(window.innerWidth - tooltipWidth - 12, event.clientX + 14),
      ),
      lines: taxMarkerLines(strategy, point, taxPaid),
      top: Math.max(
        12,
        Math.min(window.innerHeight - tooltipHeight - 12, event.clientY + 14),
      ),
    });
  };

  return (
    <div className="chart-panel">
      <div className="chart-header">
        <h2>Portfolio Value</h2>
        <div className="legend">
          {strategies.map((strategy) => (
            <span className="legend-item" key={strategy}>
              <StrategyLineSample strategy={strategy} />
              {STRATEGY_LABELS[strategy] || strategy}
            </span>
          ))}
        </div>
      </div>
      <svg
        className="portfolio-chart-svg"
        onClick={selectReplayIndexFromEvent}
        onMouseLeave={() => {
          setHoverIndex(null);
          setTaxHover(null);
        }}
        onMouseMove={(event) => {
          setHoverIndex(stepIndexFromEvent(event));
        }}
        onPointerDown={selectReplayIndexFromEvent}
        role="img"
        viewBox={`0 0 ${width} ${height}`}
      >
        <line
          className="axis"
          x1={padding.left}
          x2={width - padding.right}
          y1={height - padding.bottom}
          y2={height - padding.bottom}
        />
        <line
          className="axis"
          x1={padding.left}
          x2={padding.left}
          y1={padding.top}
          y2={height - padding.bottom}
        />
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              className="grid-line"
              x1={padding.left}
              x2={width - padding.right}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              className="axis-label"
              dominantBaseline="middle"
              textAnchor="end"
              x={padding.left - 10}
              y={y(tick)}
            >
              {formatCurrency(tick)}
            </text>
          </g>
        ))}
        {xTickIndices.map((tickIndex) => (
          <g key={tickIndex}>
            <line
              className="axis-tick"
              x1={x(tickIndex)}
              x2={x(tickIndex)}
              y1={height - padding.bottom}
              y2={height - padding.bottom + 6}
            />
            <text
              className="axis-label x-axis-label"
              textAnchor="middle"
              x={x(tickIndex)}
              y={height - 16}
            >
              {formatAxisDate(dateByIndex.get(tickIndex))}
            </text>
          </g>
        ))}
        {strategies.map((strategy) => {
          const style = STRATEGY_LINE_STYLES[strategy] || {};
          const fullSeries = seriesByStrategy[strategy] || [];
          const finalPoint = fullSeries[fullSeries.length - 1];
          const activeSeries = fullSeries.slice(
            0,
            Math.min(replayIndex, fullSeries.length - 1) + 1,
          );
          const fullPath = fullSeries
            .map((point, index) => {
              const prefix = index === 0 ? "M" : "L";
              return `${prefix}${x(index)} ${y(Number(point.portfolio_value))}`;
            })
            .join(" ");
          const activePath = activeSeries
            .map((point, index) => {
              const prefix = index === 0 ? "M" : "L";
              return `${prefix}${x(index)} ${y(Number(point.portfolio_value))}`;
            })
            .join(" ");
          return (
            <g key={strategy}>
              <path
                className="chart-line-full"
                d={fullPath}
                fill="none"
                stroke={STRATEGY_COLORS[strategy] || "#333"}
                strokeDasharray={style.dasharray || undefined}
                strokeWidth={style.strokeWidth || 2.5}
                vectorEffect="non-scaling-stroke"
              />
              <path
                className="chart-line-active"
                d={activePath}
                fill="none"
                stroke={STRATEGY_COLORS[strategy] || "#333"}
                strokeDasharray={style.dasharray || undefined}
                strokeWidth={(style.strokeWidth || 2.5) + 0.5}
                vectorEffect="non-scaling-stroke"
              />
              {hoverIndex !== null && fullSeries[cursorIndex] ? (
                <circle
                  className="chart-marker"
                  cx={x(cursorIndex)}
                  cy={y(Number(fullSeries[cursorIndex].portfolio_value))}
                  fill={STRATEGY_COLORS[strategy] || "#333"}
                  r="4"
                />
              ) : null}
              {isFinalLiquidationPoint(
                strategy,
                finalPoint,
                fullSeries.length - 1,
                fullSeries,
              ) ? (
                <g
                  className="liquidation-marker"
                  transform={`translate(${x(fullSeries.length - 1)} ${y(Number(finalPoint.portfolio_value))})`}
                >
                  <title>
                    {`${STRATEGY_LABELS[strategy] || strategy}: final liquidation after modeled costs and taxes`}
                  </title>
                  <path
                    d="M 0 -6 L 6 0 L 0 6 L -6 0 Z"
                    fill={STRATEGY_COLORS[strategy] || "#333"}
                  />
                </g>
              ) : null}
            </g>
          );
        })}
        <rect
          className="chart-hitbox"
          height={height - padding.top - padding.bottom}
          width={width - padding.left - padding.right}
          x={padding.left}
          y={padding.top}
        />
        {strategies.map((strategy, strategyIndex) =>
          (seriesByStrategy[strategy] || []).map((point, index) => {
            const taxPaid = taxPaymentAmount(point);
            const portfolioValue = Number(point.portfolio_value);
            if (taxPaid <= 0 || !Number.isFinite(portfolioValue)) {
              return null;
            }
            const marker = taxMarkerPosition(
              index,
              portfolioValue,
              strategyIndex,
            );
            return (
              <g
                aria-label={taxMarkerAriaLabel(strategy, point, taxPaid)}
                className="tax-marker"
                key={`${strategy}-tax-${index}`}
                onMouseEnter={(event) =>
                  showTaxTooltip(event, strategy, point, taxPaid)
                }
                onMouseLeave={() => setTaxHover(null)}
                onMouseMove={(event) =>
                  showTaxTooltip(event, strategy, point, taxPaid)
                }
              >
                <line
                  className="tax-marker-stem"
                  x1={x(index)}
                  x2={marker.x}
                  y1={y(portfolioValue)}
                  y2={marker.y}
                />
                <g transform={`translate(${marker.x} ${marker.y})`}>
                  <circle className="tax-marker-ring" r="8" />
                  <text className="tax-marker-text" y="0.5">
                    $
                  </text>
                </g>
              </g>
            );
          }),
        )}
        <line
          className="cursor-line"
          x1={x(cursorIndex)}
          x2={x(cursorIndex)}
          y1={padding.top}
          y2={height - padding.bottom}
        />
      </svg>
      {hasFinalLiquidation || hasTaxPayments ? (
        <p className="chart-note">
          {hasFinalLiquidation
            ? "Diamond markers show final liquidation points. "
            : ""}
          {hasTaxPayments
            ? "Orange $ markers show tax paid at that replay step."
            : ""}
        </p>
      ) : null}
      {taxHover ? (
        <div
          className="tax-tooltip"
          role="tooltip"
          style={{
            left: `${taxHover.left}px`,
            top: `${taxHover.top}px`,
          }}
        >
          <strong>{taxHover.lines[0]}</strong>
          {taxHover.lines.slice(1).map((line) => (
            <span key={line}>{line}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function taxPaymentAmount(point) {
  const taxPaid = Number(point?.tax_paid);
  return Number.isFinite(taxPaid) && taxPaid > 0 ? taxPaid : 0;
}

function taxPaymentRate(point, taxPaid) {
  const portfolioValue = Number(point?.portfolio_value);
  const beforePaymentValue = portfolioValue + taxPaid;
  if (!Number.isFinite(beforePaymentValue) || beforePaymentValue <= 0) {
    return null;
  }
  return taxPaid / beforePaymentValue;
}

function optionalTaxLine(label, value, formatter = formatCurrency) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return `${label}: ${formatter(numeric)}`;
}

function optionalTaxComponentLine(label, amount, rate, gain) {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return null;
  }
  const numericRate = finiteNumberOrNull(rate);
  const numericGain = Number(gain);
  const rateText = numericRate !== null
    ? ` (${formatPercent(numericRate)})`
    : "";
  const gainText =
    Number.isFinite(numericGain) && numericGain > 0
      ? ` on ${formatCurrency(numericGain)}`
      : "";
  return `${label}: ${formatCurrency(numericAmount)}${rateText}${gainText}`;
}

function finiteNumberOrNull(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function taxMarkerLines(strategy, point, taxPaid) {
  const rate = taxPaymentRate(point, taxPaid);
  const hasTaxComponents = [
    point?.federal_short_term_tax_paid,
    point?.federal_long_term_tax_paid,
    point?.federal_niit_tax_paid,
    point?.state_tax_paid,
  ].some((value) => Number(value) > 0);
  return [
    `${STRATEGY_LABELS[strategy] || strategy} ${point?.date || ""}`.trim(),
    `Paid: ${formatCurrency(taxPaid)}`,
    !hasTaxComponents && rate !== null
      ? `Rate: ${formatPercent(rate)} of pre-payment value`
      : null,
    optionalTaxComponentLine(
      "Fed short",
      point?.federal_short_term_tax_paid,
      point?.federal_short_term_tax_rate ?? TAX_RATE_FALLBACKS.federalShortTerm,
      point?.short_term_gain_taxed_paid,
    ),
    optionalTaxComponentLine(
      "Fed long",
      point?.federal_long_term_tax_paid,
      point?.federal_long_term_tax_rate ?? TAX_RATE_FALLBACKS.federalLongTerm,
      point?.long_term_gain_taxed_paid,
    ),
    optionalTaxComponentLine(
      "NIIT",
      point?.federal_niit_tax_paid,
      point?.federal_niit_tax_rate ?? TAX_RATE_FALLBACKS.federalNiit,
      Number(point?.short_term_gain_taxed_paid || 0) +
        Number(point?.long_term_gain_taxed_paid || 0),
    ),
    optionalTaxComponentLine(
      "CO",
      point?.state_tax_paid,
      point?.state_tax_effective_rate_paid ?? TAX_RATE_FALLBACKS.state,
      Number(point?.short_term_gain_taxed_paid || 0) +
        Number(point?.long_term_gain_taxed_paid || 0),
    ),
    optionalTaxLine("Value", point?.portfolio_value),
    optionalTaxLine("Accrued", point?.tax_accrued),
  ].filter(Boolean);
}

function taxMarkerAriaLabel(strategy, point, taxPaid) {
  return taxMarkerLines(strategy, point, taxPaid).join(" ");
}

function isFinalLiquidationPoint(strategy, point, index, fullSeries) {
  if (!point || strategy === "nasdaq100" || strategy === "nasdaq_composite") {
    return false;
  }
  if (index !== fullSeries.length - 1 || index <= 0) {
    return false;
  }
  const previous = fullSeries[index - 1] || {};
  return (
    Number(previous.positions || 0) > 0 &&
    Number(point.positions || 0) === 0 &&
    Number(point.cash_weight || 0) >= 0.99
  );
}

function StrategyLineSample({ strategy }) {
  const style = STRATEGY_LINE_STYLES[strategy] || {};
  return (
    <svg
      aria-hidden="true"
      className="strategy-line-sample"
      focusable="false"
      viewBox="0 0 30 8"
    >
      <line
        stroke={STRATEGY_COLORS[strategy] || "#333"}
        strokeDasharray={style.dasharray || undefined}
        strokeLinecap="round"
        strokeWidth={style.strokeWidth || 2.5}
        x1="2"
        x2="28"
        y1="4"
        y2="4"
      />
    </svg>
  );
}

function StrategyInfoButton({ onClick, strategy }) {
  const label = STRATEGY_LABELS[strategy] || strategy;
  return (
    <button
      aria-label={`Explain ${label}`}
      className="inline-info-button tooltip-button"
      data-tooltip={`Explain ${label}`}
      onClick={onClick}
      type="button"
    >
      <Info size={14} />
    </button>
  );
}

function StrategyInfoDialog({ onClose, strategy }) {
  const label = STRATEGY_LABELS[strategy] || strategy;
  const description =
    STRATEGY_DESCRIPTIONS[strategy] ||
    "Strategy details are not available for this artifact.";
  return (
    <FloatingInfo onClose={onClose} title={label}>
      <p>{description}</p>
    </FloatingInfo>
  );
}

function SimulationInfoDialog({ onClose }) {
  return (
    <FloatingInfo onClose={onClose} title="Simulation Details">
      <ul>
        {SIMULATION_NOTES.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </FloatingInfo>
  );
}

function FloatingInfo({ children, onClose, title, wide = false }) {
  return (
    <div className="floating-info-backdrop" role="presentation">
      <section
        aria-modal="true"
        className={`floating-info ${wide ? "floating-info-wide" : ""}`}
        role="dialog"
      >
        <header className="floating-info-header">
          <h2>{title}</h2>
          <button
            aria-label="Close"
            className="icon-button tooltip-button"
            data-tooltip="Close"
            onClick={onClose}
            type="button"
          >
            <X size={18} />
          </button>
        </header>
        <div className="floating-info-body">{children}</div>
      </section>
    </div>
  );
}

function formatAxisDate(date) {
  return date ? String(date).slice(0, 7) : "";
}

function formatScore(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(3) : "na";
}

function formatSignedNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return "na";
  }
  return `${number > 0 ? "+" : ""}${formatNumber(number)}`;
}

function decisionReason(row) {
  return String(row.reason || row.skip_reason || "na").replaceAll("_", " ");
}

function isFormControl(target) {
  const element = target instanceof Element ? target : null;
  if (!element) {
    return false;
  }
  return Boolean(
    element.closest("input, select, textarea, button, [contenteditable='true']"),
  );
}

function pageKeyFromLocation() {
  const path = window.location.pathname;
  if (path === "/methodology" || path === "/methodology.html") {
    return "methodology";
  }
  if (path === "/portfolios") {
    return "portfolios";
  }
  return "replay";
}

function replayDate(seriesByStrategy, replayIndex) {
  for (const points of Object.values(seriesByStrategy)) {
    const point = points[Math.min(replayIndex, points.length - 1)];
    if (point?.date) {
      return point.date;
    }
  }
  return "";
}
